import { useDispatch, useSelector } from 'react-redux';

import cx from 'classnames';
import { Store } from 'components/common/types/Store.types';
import { updateNewFrameTags } from 'store/pages/contentManagement/creativesAdditionalData/reducer';
import Button, { Size, Color } from 'lib/Button';
import plusSvg from 'assets/icons/plus.svg';
import minusSvg from 'assets/icons/minus.svg';
import closeSvg from 'assets/icons/close.svg';
import { FrameTagType } from 'components/common/types/FrameTag.types';
import IconButton from 'lib/IconButton';
import { Theme } from 'lib/IconButton/IconButton.types';
import FrameTagCardProps from './FrameTagCard.types';

const FrameTagCard: React.FC<FrameTagCardProps> = ({ frameTag, dataTestId, isDisabled }) => {
  const dispatch = useDispatch();
  const newFrameTags = useSelector(
    (state: Store) => state.pages.contentManagement.creativesAdditionalData.newFrameTags,
  );

  const onTypeChange = (newType: FrameTagType) => (): void => {
    const tags = newFrameTags.map((tag) => ({ ...tag }));
    const targetFrameTagIndex = tags.findIndex((tag) => tag.id === frameTag.id);

    if (targetFrameTagIndex !== -1) {
      tags[targetFrameTagIndex].type = newType;
      dispatch(updateNewFrameTags(tags));
    }
  };

  const onRemoveFrameTag = (): void => {
    const tags = newFrameTags.map((tag) => ({ ...tag }));
    const targetFrameTagIndex = tags.findIndex((tag) => tag.id === frameTag.id);

    if (targetFrameTagIndex !== -1) {
      tags.splice(targetFrameTagIndex, 1);
      dispatch(updateNewFrameTags(tags));
    }
  };

  const isFrameAvailable = frameTag.frameCount > 0;

  return (
    <div
      data-test-id={dataTestId}
      className="relative w-full bg-neutral-100 border border-neutral-300 rounded-md py-1.5 px-3 my-[2px] body-base text-neutral-950-opacity-60 flex justify-between items-center"
    >
      <div className="relative flex flex-wrap">
        <p className="mr-1">{frameTag.name}</p>
        <p
          className={cx({
            'text-danger': !isFrameAvailable,
            'text-success': isFrameAvailable,
          })}
        >
          ({frameTag.frameCount} frames)
        </p>
      </div>
      <div className="flex space-x-1 mr-1.5">
        <IconButton
          dataTestId="frame-tag-card-button-inclusion"
          handleOnClick={onTypeChange('inclusion')}
          isDisabled={isDisabled || !isFrameAvailable}
          name="frame-tag-card-button-inclusion"
          icon={plusSvg}
          theme={frameTag.type === 'inclusion' ? Theme.PRIMARY : Theme.TRANSPARENT}
        />
        <IconButton
          name="frame-tag-card-button-exclusion"
          handleOnClick={onTypeChange('exclusion')}
          icon={minusSvg}
          dataTestId="frame-tag-card-button-exclusion"
          isDisabled={isDisabled || !isFrameAvailable}
          theme={frameTag.type === 'exclusion' ? Theme.PRIMARY : Theme.TRANSPARENT}
        />
      </div>
      <div className="absolute w-2 h-2 -top-2 right-0 duration-300 opacity-0 hover:opacity-100">
        <Button
          onClick={onRemoveFrameTag}
          size={Size.EXTRA_SMALL}
          color={Color.SECONDARY}
          svg={closeSvg}
          svgClassnames="h-2 w-2"
        />
      </div>
    </div>
  );
};

export default FrameTagCard;
